<template>
  <AppLayout :breadcrumbs="[{ label: 'Tickets', link: '/tickets' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar v-model="searchTerm" @update:modelValue="handleSearch" placeholder="Search tickets..." />
        <FilterSelector :modelValue="selectedFilters" @update:modelValue="handleFilterChange" :field-options="fieldOptions" />
      </div>
      <div class="header-right">
        <button class="button icon-button" @click="openColumnSettingsModal" title="Column Settings">
          <CogIcon :size="18" />
        </button>
        <button class="button" @click="openExportModal" :disabled="totalTickets === 0">
          Export
        </button>
        <button class="button primary" @click="handleCreateTicket">
          Create Ticket
        </button>
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="visibleColumnsFiltered" :rows="tickets" :sticky-first-column="true" :loading="isLoading"
        :is-empty="tickets.length === 0" :sort-by="sortBy" :sort-order="sortOrder" @row-click="handleRowClick" @sort="handleSort">
        <template #column-ticketNumber="{ row }">
          <span class="cell-value name-text">Ticket #{{ row.ticketId }}</span>
        </template>

        <template #column-date="{ row }">
          <span class="cell-value">{{ formatUtils.formatDate(row.dateTicketStart) }}</span>
        </template>

        <template #column-ticketType="{ row }">
          <span class="cell-value">{{ getTicketType(row) }}</span>
        </template>

        <template #column-patient="{ row }">
          <div class="patient-cell">
            <IconCircle :size="28" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)" :initials="getPatientInitials(row)" />
            <span class="cell-value">{{ getPatientName(row) }}</span>
          </div>
        </template>

        <template #column-counselor="{ row }">
          <span class="cell-value">{{ row.counselorName }}</span>
        </template>

        <template #column-status="{ row }">
          <StatusBadge :status="calculateTicketStatus(row)" />
        </template>

        <template #column-products="{ row }">
          <span class="cell-value products-cell">{{ formatProducts(row.products) }}</span>
        </template>

        <template #column-paymentDuration="{ row }">
          <span class="cell-value">{{ formatPaymentDuration(row) }}</span>
        </template>

        <template #column-total="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsTotal) }}</span>
        </template>

        <template #column-collected="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCollected) }}</span>
        </template>

        <template #column-cancelled="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsCancelled) }}</span>
        </template>

        <template #column-balance="{ row }">
          <span class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance) }}</span>
        </template>

        <template #column-dateNextPayment="{ row }">
          <span v-if="row.dateNextReceivable" class="cell-value">{{ formatUtils.formatDate(row.dateNextReceivable) }}</span>
          <span v-else class="no-data">No balance</span>
        </template>

      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalTickets" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <ExportDataModal
              v-if="modal.id === 'export'"
              entity-type="tickets"
              :columns="columns"
              :current-page-count="tickets.length"
              :total-count="totalTickets"
              :export-config="exportConfig"
              :is-exporting="isExporting"
              :export-progress="exportProgress"
              :error="exportError"
              @export="handleExport"
            />
            <ColumnSettingsModal
              v-else-if="modal.id === 'columnSettings'"
              :columns="columns"
              :storage-key="`shawncrm_columns_${practiceId}_tickets`"
              :initial-visibility="columnVisibility"
              @update:visibility="handleColumnVisibilityChange"
            />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import FilterSelector from '../../components/common/FilterSelector.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import ExportDataModal from '../../modals/ExportDataModal.vue';
import ColumnSettingsModal from '../../modals/ColumnSettingsModal.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { calculateTicketStatus } from '@/utils/formatters';
import { generateCSV, downloadCSV } from '@/utils/csvExport';
import { serializeFilters, deserializeFilters } from '@/utils/filterSerializer';
import { getLocalToday } from '@/utils/dateUtils';

export default {
  name: 'TicketListView',
  data() {
    return {
      searchTerm: '',
      itemsPerPage: 100,
      currentPage: 1,
      totalTickets: 0,
      tickets: [],
      isLoading: false,
      sortBy: 'dateTicketStart',
      sortOrder: 'desc',
      selectedFilters: [],
      columnVisibility: {},
      // Modal state
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Export state
      isExporting: false,
      exportProgress: { current: 0, total: 0 },
      exportError: null,
      fieldOptions: [
        {
          id: 'dateTicketStart',
          label: 'Ticket Date',
          type: 'date',
          apiParams: {
            from: 'dateTicketStartAfter',
            to: 'dateTicketStartBefore'
          }
        },
        {
          id: 'ticketType',
          label: 'Ticket Type',
          type: 'select',
          apiParams: {
            param: 'ticketType'
          },
          options: [
            { value: 'new_patient', label: 'New Patient' },
            { value: 'current_patient', label: 'Current Patient' }
          ]
        },
        {
          id: 'counselorId',
          label: 'Counselor',
          type: 'select',
          apiParams: {
            param: 'counselorId'
          },
          options: []
        },
        {
          id: 'productIds',
          label: 'Products',
          type: 'multiselect',
          apiParams: {
            param: 'productIds',
            mode: 'productFilterMode'
          },
          options: []
        },
        {
          id: 'amountCentsTotal',
          label: 'Total Amount',
          type: 'currency',
          apiParams: {
            from: 'amountCentsTotalGte',
            to: 'amountCentsTotalLte'
          }
        },
        {
          id: 'amountCentsCollected',
          label: 'Amount Collected',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCollectedGte',
            to: 'amountCentsCollectedLte'
          }
        },
        {
          id: 'amountCentsCancelled',
          label: 'Amount Cancelled',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCancelledGte',
            to: 'amountCentsCancelledLte'
          }
        },
        {
          id: 'amountCentsBalance',
          label: 'Current Balance',
          type: 'currency',
          apiParams: {
            from: 'amountCentsBalanceGte',
            to: 'amountCentsBalanceLte'
          }
        },
        {
          id: 'dateNextReceivable',
          label: 'Date Next Payment',
          type: 'date',
          apiParams: {
            from: 'dateNextReceivableAfter',
            to: 'dateNextReceivableBefore'
          }
        }
      ],
      columns: [
        { key: 'ticketNumber', label: 'Ticket ID', width: '200px' },
        { key: 'date', label: 'Ticket Date', width: '200px', sortable: true, sortKey: 'dateTicketStart' },
        { key: 'ticketType', label: 'Ticket Type', width: '200px' },
        { key: 'patient', label: 'Patient', width: '200px' },
        { key: 'counselor', label: 'Counselor', width: '200px' },
        { key: 'status', label: 'Status', width: '200px' },
        { key: 'products', label: 'Products', width: '200px' },
        { key: 'paymentDuration', label: 'Payment Duration', width: '200px', description: 'Length of the payment plan in months' },
        { key: 'total', label: 'Total Amount', width: '200px', sortable: true, sortKey: 'amountCentsTotal' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected', description: 'Total amount successfully collected from payments' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled', description: 'Total amount cancelled from this ticket' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance', description: 'Amount still owed: total initially owed minus amount paid minus amount cancelled' },
        { key: 'dateNextPayment', label: 'Date Next Payment', width: '200px', sortable: true, sortKey: 'dateNextReceivable', description: 'Due date of the next scheduled receivable' }
      ],
      // Export column mapping
      exportConfig: {
        ticketNumber: {
          label: 'Ticket ID',
          format: (row) => `Ticket #${row.ticketId}`
        },
        date: {
          label: 'Ticket Date',
          format: (row) => formatUtils.formatDate(row.dateTicketStart)
        },
        ticketType: {
          label: 'Ticket Type',
          format: (row) => row.isFirstTicketForPatient ? 'New Patient' : 'Current Patient'
        },
        patient: {
          label: 'Patient',
          format: (row) => `${row.patientFirstName || ''} ${row.patientLastName || ''}`.trim() || 'Unknown'
        },
        counselor: {
          label: 'Counselor',
          format: (row) => row.counselorName || ''
        },
        status: {
          label: 'Status',
          format: (row) => calculateTicketStatus(row)
        },
        products: {
          label: 'Products',
          format: (row) => row.products?.map(p => `${p.name} (x${p.quantity})`).join(', ') || ''
        },
        paymentDuration: {
          label: 'Payment Duration',
          format: (row) => {
            if (!row.dateFirstReceivable || !row.dateLastReceivable) {
              return '—';
            }
            const first = new Date(row.dateFirstReceivable);
            const last = new Date(row.dateLastReceivable);
            const months = (last.getFullYear() - first.getFullYear()) * 12 + (last.getMonth() - first.getMonth()) + 1;
            if (months <= 1) {
              return 'Pay in Full';
            }
            return `${months} Months`;
          }
        },
        total: {
          label: 'Total Amount',
          format: (row) => formatUtils.formatCurrency(row.amountCentsTotal)
        },
        collected: {
          label: 'Amount Collected',
          format: (row) => formatUtils.formatCurrency(row.amountCentsCollected)
        },
        cancelled: {
          label: 'Amount Cancelled',
          format: (row) => formatUtils.formatCurrency(row.amountCentsCancelled)
        },
        balance: {
          label: 'Current Balance',
          format: (row) => formatUtils.formatCurrency(row.amountCentsBalance)
        },
        dateNextPayment: {
          label: 'Date Next Payment',
          format: (row) => row.dateNextReceivable ? formatUtils.formatDate(row.dateNextReceivable) : ''
        }
      },
      formatUtils
    };
  },
  computed: {
    practiceId() {
      return api.getPracticeId();
    },
    visibleColumnsFiltered() {
      return this.columns.filter(col => {
        // Always show first column (ticketNumber)
        if (col.key === 'ticketNumber') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.columnVisibility[col.key] !== false;
      });
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;

        const urlFilters = deserializeFilters(newQuery.filters);
        const urlSearch = newQuery.search || '';
        const urlPage = parseInt(newQuery.page) || 1;
        const urlSortBy = newQuery.sortBy || 'dateTicketStart';
        const urlSortOrder = newQuery.sortOrder || 'desc';

        const hasChanges =
          JSON.stringify(urlFilters) !== JSON.stringify(this.selectedFilters) ||
          urlSearch !== this.searchTerm ||
          urlPage !== this.currentPage ||
          urlSortBy !== this.sortBy ||
          urlSortOrder !== this.sortOrder;

        if (!hasChanges) return;

        this.selectedFilters = urlFilters;
        this.searchTerm = urlSearch;
        this.currentPage = urlPage;
        this.sortBy = urlSortBy;
        this.sortOrder = urlSortOrder;

        this.loadTickets();
      }
    }
  },
  async created() {
    // Check if URL has any state params
    const hasUrlParams =
      this.$route.query.filters !== undefined ||
      this.$route.query.search !== undefined ||
      this.$route.query.sortBy !== undefined ||
      this.$route.query.sortOrder !== undefined;

    if (hasUrlParams) {
      // Load from URL (existing logic)
      if (this.$route.query.filters !== undefined) {
        this.selectedFilters = deserializeFilters(this.$route.query.filters);
      }
      if (this.$route.query.search !== undefined) {
        this.searchTerm = this.$route.query.search || '';
      }
      if (this.$route.query.sortBy !== undefined) {
        this.sortBy = this.$route.query.sortBy || 'dateTicketStart';
      }
      if (this.$route.query.sortOrder !== undefined) {
        this.sortOrder = this.$route.query.sortOrder || 'desc';
      }
    } else {
      // Load from localStorage
      const savedState = this.loadFiltersFromLocalStorage();
      if (savedState) {
        this.selectedFilters = savedState.selectedFilters || [];
        this.sortBy = savedState.sortBy || 'dateTicketStart';
        this.sortOrder = savedState.sortOrder || 'desc';

        // Immediately sync to URL
        this.syncStateToUrl();
      }
    }

    this.currentPage = parseInt(this.$route.query.page) || 1;

    // Load column visibility settings
    this.loadColumnVisibility();

    // Load options for select/multiselect filters
    await this.loadFilterOptions();

    // Load tickets
    this.loadTickets();
  },
  methods: {
    async loadFilterOptions() {
      try {
        // Load users for counselor filter
        const usersResponse = await queryClient.fetchQuery({
          queryKey: ['users'],
          queryFn: () => api.fetchUsers()
        });

        const counselorField = this.fieldOptions.find(f => f.id === 'counselorId');
        if (counselorField && usersResponse.users) {
          counselorField.options = usersResponse.users.map(user => ({
            value: user.userId,
            label: user.name || 'Unknown'
          }));
        }

        // Load products for product filter
        const productsResponse = await queryClient.fetchQuery({
          queryKey: ['products'],
          queryFn: () => api.fetchProducts()
        });

        const productField = this.fieldOptions.find(f => f.id === 'productIds');
        if (productField && productsResponse.products) {
          productField.options = productsResponse.products.map(product => ({
            value: product.productId,
            label: product.name
          }));
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    },
    getFilterParams() {
      const params = {};

      this.selectedFilters.forEach(filter => {
        const fieldConfig = this.fieldOptions.find(f => f.id === filter.field);
        if (!fieldConfig || !filter.operator) return;

        const fieldType = fieldConfig.type;

        if (fieldType === 'date' || fieldType === 'currency') {
          if (filter.operator === 'between') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'after' || filter.operator === 'gte') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
          } else if (filter.operator === 'before' || filter.operator === 'lte') {
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'is' && fieldType === 'date') {
            if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
              const formattedValue = this.formatParamValue(filter.value, fieldType);
              params[fieldConfig.apiParams.from] = formattedValue;
              params[fieldConfig.apiParams.to] = formattedValue;
            }
          }
        } else if (fieldType === 'select') {
          if (filter.value !== null && filter.value !== undefined) {
            params[fieldConfig.apiParams.param] = filter.value;
          }
        } else if (fieldType === 'multiselect') {
          if (filter.values && filter.values.length > 0 && filter.operator) {
            params[fieldConfig.apiParams.param] = filter.values.join(',');
            params[fieldConfig.apiParams.mode] = filter.operator; // 'any' or 'all'
          }
        }
      });

      return params;
    },
    formatParamValue(value, fieldType) {
      if (value === null || value === undefined || value === '') return null;

      if (fieldType === 'date') {
        return value;
      } else if (fieldType === 'currency') {
        return value;
      }

      return value;
    },
    syncStateToUrl() {
      const query = {};

      const serialized = serializeFilters(this.selectedFilters);
      if (serialized) {
        query.filters = serialized;
      }

      if (this.searchTerm) {
        query.search = this.searchTerm;
      }

      if (this.currentPage > 1) {
        query.page = this.currentPage;
      }

      if (this.sortBy !== 'dateTicketStart') {
        query.sortBy = this.sortBy;
      }

      if (this.sortOrder !== 'desc') {
        query.sortOrder = this.sortOrder;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    async loadTickets() {
      try {
        this.isLoading = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const filterParams = this.getFilterParams();

        const response = await queryClient.fetchQuery({
          queryKey: ['tickets', {
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          }],
          queryFn: () => api.fetchTickets({
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          })
        });

        this.tickets = response.tickets;
        this.totalTickets = response.total;

      } catch (err) {
        console.error('Failed to fetch tickets:', err);
        alertService.error('Failed to load tickets: ' + err.message);
        this.tickets = [];
        this.totalTickets = 0;
      } finally {
        this.isLoading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadTickets();
    },
    handleFilterChange(filters) {
      this.selectedFilters = filters;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadTickets();
    },
    handleCreateTicket() {
      this.$router.push('/tickets/new');
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncStateToUrl();
      this.loadTickets();
    },
    handleRowClick({ rowIndex }) {
      const ticket = this.tickets[rowIndex];
      if (ticket && ticket.ticketId) {
        this.$router.push(`/tickets/${ticket.ticketId}`);
      }
    },
    handleSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadTickets();
    },
    saveFiltersToLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        // Store filters as serialized string (same as URL)
        const serialized = serializeFilters(this.selectedFilters);
        if (serialized) {
          localStorage.setItem(`shawncrm_filters_${practiceId}_tickets`, serialized);
        } else {
          localStorage.removeItem(`shawncrm_filters_${practiceId}_tickets`);
        }

        // Store sort settings
        localStorage.setItem(`shawncrm_sortBy_${practiceId}_tickets`, this.sortBy);
        localStorage.setItem(`shawncrm_sortOrder_${practiceId}_tickets`, this.sortOrder);
      } catch (err) {
        console.error('Failed to save filters to localStorage:', err);
      }
    },
    loadFiltersFromLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return null;

      try {
        const filtersStr = localStorage.getItem(`shawncrm_filters_${practiceId}_tickets`);
        const sortBy = localStorage.getItem(`shawncrm_sortBy_${practiceId}_tickets`);
        const sortOrder = localStorage.getItem(`shawncrm_sortOrder_${practiceId}_tickets`);

        return {
          selectedFilters: filtersStr ? deserializeFilters(filtersStr) : [],
          sortBy: sortBy || 'dateTicketStart',
          sortOrder: sortOrder || 'desc'
        };
      } catch (err) {
        console.error('Failed to load filters from localStorage:', err);
        return null;
      }
    },
    loadColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_tickets`);
        if (saved) {
          this.columnVisibility = JSON.parse(saved);
        } else {
          // Default columns: Ticket Date, Ticket Type, Patient, Counselor, Status, Products, Total Amount, Current Balance
          const defaultVisibleColumns = ['date', 'ticketType', 'patient', 'counselor', 'status', 'products', 'total', 'balance'];
          this.columns.forEach(col => {
            this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['date', 'ticketType', 'patient', 'counselor', 'status', 'products', 'total', 'balance'];
        this.columns.forEach(col => {
          this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    },
    saveColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        localStorage.setItem(
          `shawncrm_columns_${practiceId}_tickets`,
          JSON.stringify(this.columnVisibility)
        );
      } catch (err) {
        console.error('Failed to save column visibility:', err);
      }
    },
    handleColumnVisibilityChange(visibility) {
      this.columnVisibility = visibility;
      this.saveColumnVisibility();
    },
    getPatientInitials(ticket) {
      const first = ticket.patientFirstName?.charAt(0) || '';
      const last = ticket.patientLastName?.charAt(0) || '';
      return (first + last).toUpperCase();
    },
    getCounselorInitials(ticket) {
      const name = ticket.counselorName?.charAt(0) || '';
      return name.toUpperCase();
    },
    getPatientName(ticket) {
      const first = ticket.patientFirstName || '';
      const last = ticket.patientLastName || '';
      return `${first} ${last}`.trim() || 'Unknown Patient';
    },
    calculateTicketStatus(ticket) {
      return calculateTicketStatus(ticket);
    },
    formatPaymentDuration(ticket) {
      if (!ticket.dateFirstReceivable || !ticket.dateLastReceivable) {
        return '—';
      }
      const first = new Date(ticket.dateFirstReceivable);
      const last = new Date(ticket.dateLastReceivable);
      const months = (last.getFullYear() - first.getFullYear()) * 12 + (last.getMonth() - first.getMonth()) + 1;
      if (months <= 1) {
        return 'Pay in Full';
      }
      return `${months} Months`;
    },
    formatProducts(products) {
      if (!products || products.length === 0) {
        return '—';
      }
      return products.map(p => `${p.name} (x${p.quantity})`).join(', ');
    },
    getTicketType(ticket) {
      return ticket.isFirstTicketForPatient ? 'New Patient' : 'Current Patient';
    },
    // Modal management
    openColumnSettingsModal() {
      this.openModal('columnSettings', {});
    },
    openExportModal() {
      this.openModal('export', {});
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    // Export modal methods
    async handleExport({ scope, columns }) {
      this.isExporting = true;
      this.exportError = null;
      this.exportProgress = { current: 0, total: 0 };

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let dataToExport = [];

        if (scope === 'current') {
          dataToExport = this.tickets;
          this.exportProgress = { current: this.tickets.length, total: this.tickets.length };
        } else {
          const filterParams = this.getFilterParams();
          const pageSize = 100;

          const firstPageResponse = await api.fetchTickets({
            limit: pageSize,
            offset: 0,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          });

          const totalRecords = firstPageResponse.total;
          const totalPages = Math.ceil(totalRecords / pageSize);

          this.exportProgress = { current: 0, total: totalRecords };
          await this.$nextTick();

          dataToExport = [...firstPageResponse.tickets];
          this.exportProgress.current = firstPageResponse.tickets.length;
          await this.$nextTick();

          for (let page = 1; page < totalPages; page++) {
            await new Promise(resolve => setTimeout(resolve, 100));

            const offset = page * pageSize;
            const response = await api.fetchTickets({
              limit: pageSize,
              offset,
              search: this.searchTerm,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              ...filterParams
            });

            dataToExport = [...dataToExport, ...response.tickets];
            this.exportProgress.current = dataToExport.length;
            await this.$nextTick();
          }
        }

        // Generate CSV
        const csvContent = generateCSV(dataToExport, columns, this.exportConfig);

        // Download CSV
        const timestamp = getLocalToday();
        const filename = `tickets-export-${timestamp}`;
        downloadCSV(csvContent, filename);

        // Show success message
        alertService.success(`Successfully exported ${dataToExport.length} tickets`);

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
          this.isExporting = false;
          this.exportProgress = { current: 0, total: 0 };
        }, 500);

      } catch (err) {
        console.error('Export failed:', err);
        this.exportError = err.message || 'Failed to export data. Please try again.';
        this.isExporting = false;
      }
    }
  },
  components: {
    AppLayout,
    SearchBar,
    FilterSelector,
    CustomTable,
    PaginationControl,
    IconCircle,
    StatusBadge,
    ExportDataModal,
    ColumnSettingsModal,
    CogIcon
  }
}
</script>

<style scoped>
/* Header section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-width: 32px;
}

/* Cell styling */
.ticket-cell,
.counselor-cell,
.patient-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-text {
  font-size: 12px;
  font-weight: 500;
}

/* Cell value styling */
.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.products-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}
</style>
